import { useEffect, useCallback } from 'react';
import ControlWebSocketHandler from '@/bussiness/ControlWebSocketHandler';
import deviceStore from '@/store/device.store';
import { getControlConnection } from '@/services/api_control';

interface UseControlWebSocketOptions {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onErrorDisconnect?: () => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
  onRecordingPaused?: () => void;
  onRecordingResumed?: () => void;
  onDurationUpdate?: (duration: number) => void;
}

/**
 * 同传控制 WebSocket Hook (单例模式)
 * 负责与控制子进程建立 WebSocket 连接，发送控制指令并接收状态更新
 * 使用单例 WebSocketManager，确保全局只有一个 WebSocket 连接
 */
export const useControlWebSocket = (options: UseControlWebSocketOptions = {}) => {
  const {
    onConnected,
    onDisconnected,
    onErrorDisconnect,
    onRecordingStarted,
    onRecordingStopped,
    onRecordingPaused,
    onRecordingResumed,
    onDurationUpdate,
  } = options;

  // 注册回调到 Handler
  useEffect(() => {
    ControlWebSocketHandler.setCallbacks(options);
  }, [onConnected, onErrorDisconnect, onDisconnected, onRecordingStarted, onRecordingStopped, onRecordingPaused, onRecordingResumed, onDurationUpdate]);

  // 连接 WebSocket
  const connect = useCallback(async (deviceIP: string, port: string = '2998') => {
    const controlConnection = await getControlConnection(deviceIP, port);
    ControlWebSocketHandler.connect(controlConnection.wsUrl);
  }, []);

  // 断开连接
  const disconnect = useCallback(() => {
    ControlWebSocketHandler.disconnect(false); // 业务触发的断开，都是正常断开
  }, []);

  // 开始录制
  const startRecording = useCallback((streamName?: string) => {
    return ControlWebSocketHandler.startRecording(streamName);
  }, []);

  // 停止录制
  const stopRecording = useCallback(() => {
    return ControlWebSocketHandler.stopRecording();
  }, []);

  // 暂停录制
  const pauseRecording = useCallback(() => {
    return ControlWebSocketHandler.pauseRecording();
  }, []);

  // 恢复录制
  const resumeRecording = useCallback(() => {
    return ControlWebSocketHandler.resumeRecording();
  }, []);

  // 获取所有状态
  const getAllStatus = useCallback(() => {
    return ControlWebSocketHandler.getAllStatus();
  }, []);

  // 清除字幕
  const clearSubtitle = useCallback(() => {
    return ControlWebSocketHandler.clearSubtitle();
  }, []);

  return {
    connect,
    disconnect,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    getAllStatus,
    clearSubtitle,
    isConnected: deviceStore((state) => state.wsConnected),
  };
};

